/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { TGenericBlueprintSchema, TGenericModelNode } from "@adapptio/lib-blueprint";
import { IFieldTypeOption } from "../../Fields/FieldType";
import { IInspectorControls, TResolvedInspectorControlValueTypes } from "../InspectorTypes";
import { ControlActionRef } from "./ControlActionRef";
import { ControlConstAny, ControlConstAny_ResolveValueTypes } from "./ControlConstAny";
import { ControlConstArray } from "./ControlConstArray";
import { ControlConstBoolean } from "./ControlConstBoolean";
import { ControlConstEnum } from "./ControlConstEnum";
import { ControlConstFloat } from "./ControlConstFloat";
import { ControlConstInteger } from "./ControlConstInteger";
import { ControlConstMap } from "./ControlConstMap";
import { ControlConstObject } from "./ControlConstObject";
import { ControlConstString } from "./ControlConstString";
import { ControlConstExpression } from "./ControlExpression";
import { ControlIconName } from "./ControlIconName";
import { ControlIntegrationRef } from "./ControlIntegrationRef";
import { ControlOneOf } from "./ControlOneOf";
import { ControlOptGroup } from "./ControlOptGroup";
import { ControlRecursive } from "./ControlRecursive";
import { ControlResponsiveValue } from "./ControlResponsiveValue";
import { ControlRouteRef } from "./ControlRouteRef";
import { ControlSchemaBuilder } from "./ControlSchemaBuilder";
import { ControlSchemaBuilderAny } from "./ControlSchemaBuilderAny";
import { ControlScopedTemplate } from "./ControlScopedTemplate";
import { ControlStringTranslate } from "./ControlStringTranslate";
import { ControlStringWithConst } from "./ControlStringWithConst";
import { ControlValue, ControlValue_ResolveValueTypes } from "./ControlValue";
import { ControlViewRef } from "./ControlViewRef";
import { ControlConstSecret } from "./ControlConstSecret";
import { ControlConstPassword } from "./ControlConstPassword";
import { ControlComments } from "./ControlComments";

// Cache of inspector controls, created at first retrieve
// Is "asnyc" because of cycle dependencies
let _inspectorControlsMap: {
	[K: string]: (model: TGenericModelNode) => IInspectorControls;
} = null;

// Cache of inspector controls value types, created at first retrieve
// Is "asnyc" because of cycle dependencies
let _inspectorControlValueTypesMap: {
	[K: string]: TResolvedInspectorControlValueTypes;
} = null;

/**
 * Returns inspector controls map
 */
function getInspectorControlsMap() {
	if (_inspectorControlsMap !== null) {
		return _inspectorControlsMap;
	}

	return (_inspectorControlsMap = {
		actionRef: ControlActionRef,
		builderArray: ControlSchemaBuilder,
		builderBoolean: ControlSchemaBuilder,
		builderData: ControlSchemaBuilder,
		builderDate: ControlSchemaBuilder,
		builderEnum: ControlSchemaBuilder,
		builderFloat: ControlSchemaBuilder,
		builderInteger: ControlSchemaBuilder,
		builderMap: ControlSchemaBuilder,
		builderObject: ControlSchemaBuilder,
		builderPassword: ControlSchemaBuilder,
		builderString: ControlSchemaBuilder,
		builderAny: ControlSchemaBuilderAny,
		comments: ControlComments,
		constAny: ControlConstAny,
		constArray: ControlConstArray,
		constBoolean: ControlConstBoolean,
		constEnum: ControlConstEnum,
		constFloat: ControlConstFloat,
		constInteger: ControlConstInteger,
		constMap: ControlConstMap,
		constObject: ControlConstObject,
		constPassword: ControlConstPassword,
		constSecret: ControlConstSecret,
		constString: ControlConstString,
		expression: ControlConstExpression,
		iconName: ControlIconName,
		integrationRef: ControlIntegrationRef,
		oneOf: ControlOneOf,
		optGroup: ControlOptGroup,
		recursive: ControlRecursive,
		responsiveValue: ControlResponsiveValue,
		routeRef: ControlRouteRef,
		scopedTemplate: ControlScopedTemplate,
		stringTranslate: ControlStringTranslate,
		stringWithConst: ControlStringWithConst,
		value: ControlValue,
		viewRef: ControlViewRef
	});
}

/**
 * Returns inspector controls value types map
 */
function getInspectorControlsValueTypesMap() {
	if (_inspectorControlValueTypesMap !== null) {
		return _inspectorControlValueTypesMap;
	}

	return (_inspectorControlValueTypesMap = {
		constAny: ControlConstAny_ResolveValueTypes,
		value: ControlValue_ResolveValueTypes
	});
}

/**
 * Returns controls for a model
 *
 * @param model Model node
 */
export function getInspectorControls(model: TGenericModelNode): IInspectorControls {
	const controlsMap = getInspectorControlsMap();

	const schema = model?.schema;
	const controlsDef = controlsMap[schema?.name] || {};
	let controls: IInspectorControls;

	if (controlsDef && typeof controlsDef === "function") {
		controls = controlsDef(model);
	} else {
		controls = controlsDef as IInspectorControls;
	}

	return {
		primary: controls?.primary || null,
		primaryChildren: controls?.primaryChildren || null,
		children: controls?.children || null,
		headerPrefix: controls?.headerPrefix || null,
		headerControls: controls?.headerControls || null,
		valueTypes: controls?.valueTypes || null,
		dependencyModels: controls?.dependencyModels || null,
		displayInSection: controls?.displayInSection || false
	};
}

/**
 * Returns control value types for a schema
 *
 * @param schema Blueprint schema
 */
export function getInspectorControlValueTypes(schema: TGenericBlueprintSchema): IFieldTypeOption[] {
	const valueTypesMap = getInspectorControlsValueTypesMap();

	if (valueTypesMap[schema?.name]) {
		return valueTypesMap[schema?.name](schema);
	} else {
		return [];
	}
}
